import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL } from '../config/canonical';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ClipboardCheck, LogOut, Clock, CheckCircle2, AlertCircle, FileText, Inbox, Upload, Download, MailOpen, FileSignature } from 'lucide-react';
import SignDocumentModal from '../components/recruitment/SignDocumentModal';

const Radio = ({ checked, onChange, label, testId }) => (
  <label className={`flex items-center gap-3 px-4 py-3 border rounded cursor-pointer transition-colors ${checked ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
    <input type="radio" checked={checked} onChange={onChange} className="accent-amber-500" data-testid={testId} />
    <span className="text-sm text-slate-800">{label}</span>
  </label>
);

const ApplicantPortal = () => {
  const [me, setMe] = useState(null);
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [docAssignments, setDocAssignments] = useState([]);
  const [signingAsn, setSigningAsn] = useState(null);
  const [openMsg, setOpenMsg] = useState(null);
  const nav = useNavigate();
  const token = localStorage.getItem('nexus_applicant_token');

  const logout = useCallback(() => {
    localStorage.removeItem('nexus_applicant_token');
    localStorage.removeItem('nexus_applicant_id');
    nav('/applicant/login');
  }, [nav]);

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/applicant-portal/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { logout(); return; }
      // Iter136 — a 403 does NOT mean the session died; keep the user
      // signed in and let the UI render a limited empty state.
      if (!res.ok) { setLoading(false); return; }
      const data = await res.json();
      setMe(data);
    } catch { /* swallow */ } finally { setLoading(false); }
  }, [token, logout]);

  useEffect(() => {
    if (!token) { nav('/applicant/login'); return; }
    fetchMe();
    fetchInbox();
  }, [token, fetchMe, nav]);

  async function fetchInbox() {
    try {
      const [m, d, i, da] = await Promise.all([
        fetch(`${BACKEND_URL}/api/applicant-portal/messages`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/applicant-portal/documents`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/applicant-portal/interviews`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
        fetch(`${BACKEND_URL}/api/applicant-portal/document-assignments`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
      ]);
      setMessages(m.messages || []);
      setDocuments(d.documents || []);
      setInterviews(i.interviews || []);
      setDocAssignments(da.assignments || []);
    } catch { /* ignore */ }
  }

  const markRead = async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/applicant-portal/messages/${id}/read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      setMessages(prev => prev.map(m => m.id === id ? { ...m, read_at: new Date().toISOString() } : m));
    } catch { /* ignore */ }
  };

  const downloadDoc = async (docId, name) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/applicant-portal/documents/${docId}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = name || 'document';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const submitDoc = async (docId, doc) => {
    const fileInput = document.getElementById(`doc-file-${docId}`);
    const file = fileInput?.files?.[0];
    let signature = null;
    if (doc.require_signature) {
      signature = window.prompt('Type your full legal name as your e-signature:');
      if (!signature) return;
    }
    if (doc.require_upload_back && !file) {
      toast.error('Please choose a file to upload');
      return;
    }
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      if (signature) fd.append('signature_typed_name', signature);
      fd.append('acknowledgment', 'true');
      const res = await fetch(`${BACKEND_URL}/api/applicant-portal/documents/${docId}/submit`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Submit failed');
      toast.success('Document submitted');
      fetchInbox();
    } catch (e) { toast.error(e.message); }
  };

  const loadTest = async () => {
    // Require applicant to enter their NEXUS-XXXX username as unlock code
    const expected = (me?.portal_username || '').toUpperCase();
    const entered = (window.prompt(
      'To unlock the IPS-OIE exam, enter your Nexus username (NEXUS-XXXX) exactly as shown in your Nexus email:'
    ) || '').trim().toUpperCase();
    if (!entered) return;
    if (!expected || entered !== expected) {
      toast.error('Unlock code does not match your Nexus username. Check your email and try again.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/applicant-portal/test`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Test-Unlock': entered },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Test unavailable');
      setTest(data);
      toast.success('Exam unlocked — answer all 130 questions in one sitting.');
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const submit = async () => {
    // Validate — all 130 must be answered
    const unanswered = test.questions.filter(q => !answers[String(q.number)]);
    if (unanswered.length) {
      toast.error(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/applicant-portal/test/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answers }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Submit failed');
      setSubmitted(data);
      setTest(null);
      fetchMe();
    } catch (err) { toast.error(err.message); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0B192C] text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/nexus-patch.png" alt="Nexus" className="w-14 h-14 object-contain" />
          <div>
            <h1 className="font-heading font-bold">Nexus Applicant Portal</h1>
            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-300">
              {me?.first_name} {me?.last_name} • {me?.id}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={logout} className="border-white/20 text-white hover:bg-white/10" data-testid="applicant-logout-btn">
          <LogOut className="w-4 h-4 mr-1" /> Sign Out
        </Button>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Missing-info banner — application reopened by admin */}
        {me?.application_status === 'AWAITING_APPLICANT_RESUBMIT' && (
          <ResubmitPanel me={me} token={token} onDone={fetchMe} />
        )}

        {/* Interviews scheduled for this applicant */}
        {interviews.length > 0 && (
          <div className="bg-white border border-slate-300 rounded p-5" data-testid="applicant-interviews-panel">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-heading font-bold text-slate-900">Your interview{interviews.length > 1 ? 's' : ''}</h2>
              <Badge className="bg-purple-200 text-purple-950 border border-purple-400 text-[10px]">{interviews.length}</Badge>
            </div>
            <div className="space-y-3">
              {interviews.map(itv => (
                <div key={itv.id} className="border border-slate-300 rounded p-3" data-testid={`applicant-interview-${itv.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">
                        {itv.interview_type?.replace(/_/g, ' ')} interview
                      </p>
                      <p className="text-xs text-slate-700 mt-0.5">
                        {new Date(itv.scheduled_at).toLocaleString()} · {itv.duration_minutes || 45} min
                        {itv.interviewer_name ? ` · with ${itv.interviewer_name}` : ''}
                      </p>
                      {itv.location && (
                        <p className="text-xs text-slate-700 mt-1 break-all">
                          {itv.interview_type === 'VIDEO' ? 'Meeting link: ' : 'Location: '}
                          {itv.interview_type === 'VIDEO' && /^https?:\/\//.test(itv.location) ? (
                            <a href={itv.location} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{itv.location}</a>
                          ) : itv.location}
                        </p>
                      )}
                      {itv.notes && <p className="text-xs text-slate-800 mt-1">{itv.notes}</p>}
                    </div>
                    <Badge className={`text-[10px] border ${
                      itv.status === 'COMPLETED' ? (
                        itv.outcome === 'PASS' ? 'bg-emerald-200 text-emerald-900 border-emerald-400' :
                        itv.outcome === 'FAIL' ? 'bg-rose-200 text-rose-900 border-rose-400' :
                        'bg-amber-200 text-amber-900 border-amber-400'
                      ) : 'bg-purple-200 text-purple-900 border-purple-400'
                    }`}>
                      {itv.status}{itv.outcome ? ` · ${itv.outcome}` : ''}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DocuSign-style Documents to Sign */}
        {docAssignments.length > 0 && (
          <div className="bg-white border border-slate-300 rounded p-5" data-testid="applicant-docusign-panel">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <FileSignature className="w-5 h-5 text-amber-600" />
              <h2 className="text-lg font-heading font-bold text-slate-900">Documents to Sign</h2>
              <Badge className="bg-amber-200 text-amber-950 border border-amber-400 text-[10px]">
                {docAssignments.filter(a => a.status === 'PENDING').length} pending
              </Badge>
            </div>
            <div className="space-y-3">
              {docAssignments.map(a => (
                <div key={a.id} className="border border-slate-300 rounded p-3" data-testid={`applicant-sign-asn-${a.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 break-words">{a.template_name}</p>
                      <p className="text-xs text-slate-700 mt-0.5">
                        Sent: {new Date(a.sent_at).toLocaleDateString()}
                        {a.signed_at && ` • Signed: ${new Date(a.signed_at).toLocaleDateString()}`}
                        {a.sent_by_name && ` • By ${a.sent_by_name}`}
                      </p>
                      {a.note && <p className="text-xs text-slate-800 mt-1 italic">{a.note}</p>}
                    </div>
                    <Badge
                      className={
                        a.status === 'SIGNED'
                          ? 'bg-emerald-200 text-emerald-900 border border-emerald-400 text-[10px]'
                          : 'bg-amber-200 text-amber-950 border border-amber-400 text-[10px]'
                      }
                    >
                      {a.status}
                    </Badge>
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {a.status === 'PENDING' ? (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-500 text-white h-8"
                        onClick={() => setSigningAsn(a)}
                        data-testid={`applicant-sign-btn-${a.id}`}
                      >
                        <FileSignature className="w-3 h-3 mr-1" /> Review & Sign
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-slate-900 border-slate-300 h-8 text-xs"
                        onClick={async () => {
                          try {
                            const res = await fetch(
                              `${BACKEND_URL}/api/applicant-portal/document-assignments/${a.id}/file?signed=true`,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            if (!res.ok) throw new Error('Download failed');
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          } catch (e) { toast.error(e.message); }
                        }}
                        data-testid={`applicant-view-signed-${a.id}`}
                      >
                        <Download className="w-3 h-3 mr-1" /> View Signed
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents assigned by admin */}
        {documents.length > 0 && (
          <div className="bg-white border border-slate-300 rounded p-5" data-testid="applicant-documents-panel">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-amber-600" />
              <h2 className="text-lg font-heading font-bold text-slate-900">Documents for you</h2>
              <Badge className="bg-amber-200 text-amber-950 border border-amber-400 text-[10px]">{documents.filter(d => d.status === 'PENDING' || d.status === 'REJECTED').length} open</Badge>
            </div>
            <div className="space-y-3">
              {documents.map(d => (
                <div key={d.id} className="border border-slate-300 rounded p-3" data-testid={`applicant-doc-${d.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <p className="font-semibold text-slate-900">{d.label}</p>
                      <p className="text-xs text-slate-700">Category: {d.category} • Due: {d.due_date || '—'} • Status: <span className="font-semibold">{d.status}</span></p>
                      {d.notes && <p className="text-xs text-slate-800 mt-1">{d.notes}</p>}
                      {d.correction_notice && <p className="text-xs text-rose-800 mt-1 bg-rose-50 border border-rose-300 rounded p-1.5">Correction requested: {d.correction_notice}</p>}
                    </div>
                    <div className="flex flex-col gap-1">
                      {d.admin_file_name && (
                        <Button size="sm" variant="outline" className="text-slate-900 border-slate-300 h-8 text-xs" onClick={() => downloadDoc(d.id, d.admin_file_name)}>
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                      )}
                    </div>
                  </div>
                  {(d.status === 'PENDING' || d.status === 'REJECTED') && (
                    <div className="mt-3 border-t border-slate-200 pt-3 flex items-end gap-2 flex-wrap">
                      {d.require_upload_back && (
                        <input type="file" id={`doc-file-${d.id}`} className="text-xs" data-testid={`applicant-doc-file-${d.id}`} />
                      )}
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white h-8" onClick={() => submitDoc(d.id, d)} data-testid={`applicant-doc-submit-${d.id}`}>
                        <Upload className="w-3 h-3 mr-1" /> {d.require_signature ? 'Sign & Submit' : 'Submit'}
                      </Button>
                    </div>
                  )}
                  {d.status === 'SUBMITTED' && <p className="text-xs text-emerald-800 mt-2">Submitted — awaiting review.</p>}
                  {d.status === 'APPROVED' && <p className="text-xs text-emerald-800 mt-2">Approved by Nexus.</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inbox */}
        {messages.length > 0 && (
          <div className="bg-white border border-slate-300 rounded p-5" data-testid="applicant-inbox-panel">
            <div className="flex items-center gap-2 mb-3">
              <Inbox className="w-5 h-5 text-slate-700" />
              <h2 className="text-lg font-heading font-bold text-slate-900">Inbox</h2>
              <Badge className="bg-slate-200 text-slate-900 border border-slate-400 text-[10px]">{messages.filter(m => !m.read_at).length} unread</Badge>
            </div>
            <div className="divide-y divide-slate-200">
              {messages.map(m => (
                <div key={m.id} className="py-2" data-testid={`applicant-msg-${m.id}`}>
                  <button className="w-full text-left flex items-start justify-between gap-3" onClick={() => { setOpenMsg(openMsg === m.id ? null : m.id); if (!m.read_at) markRead(m.id); }}>
                    <div>
                      <p className={`text-sm ${m.read_at ? 'text-slate-800' : 'text-slate-900 font-bold'}`}>{m.subject}</p>
                      <p className="text-[11px] text-slate-600">From {m.from_name} • {new Date(m.sent_at).toLocaleString()}</p>
                    </div>
                    {!m.read_at && <Badge className="bg-amber-300 text-amber-950 border border-amber-500 text-[10px]">New</Badge>}
                    {m.read_at && <MailOpen className="w-3.5 h-3.5 text-slate-500" />}
                  </button>
                  {openMsg === m.id && (
                    <p className="text-sm text-slate-900 mt-2 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3">{m.body}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {submitted && (
          <div className="bg-white border border-emerald-300 rounded p-6 mb-6 text-center" data-testid="applicant-submitted-card">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
            <h2 className="text-xl font-heading font-bold text-slate-900">Assessment submitted</h2>
            <p className="text-sm text-slate-600 mt-1">{submitted.message}</p>
            <p className="text-xs font-mono text-slate-400 mt-2">{new Date(submitted.submitted_at).toLocaleString()}</p>
          </div>
        )}

        {!test && !submitted && (
          <div className="bg-white border border-slate-200 rounded p-6" data-testid="applicant-dashboard">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-amber-500" />
              <div>
                <h2 className="text-lg font-heading font-bold text-slate-900">Your Application Status</h2>
                <p className="text-xs text-slate-500">{me?.position_applied}</p>
              </div>
            </div>
            <div className="mb-6">
              <span className="text-xs font-mono uppercase tracking-wider text-slate-500">Current Stage</span>
              <div className="mt-1"><Badge className="bg-amber-100 text-amber-800 border-amber-300">{me?.stage?.replace(/_/g, ' ')}</Badge></div>
            </div>

            {me?.can_take_test ? (
              <div className="border border-amber-300 bg-amber-50 rounded p-5">
                <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4 text-amber-600" /> IPS-OIE Exam
                </h3>
                <p className="text-sm text-slate-700 mt-2">
                  The Investigator Psychological Suitability & Operational Integrity Exam is 130 questions and must be completed in one sitting. Answer every question honestly.
                </p>
                <Button onClick={loadTest} className="mt-4 bg-amber-500 hover:bg-amber-600 text-white" data-testid="applicant-start-test-btn">
                  Start Exam
                </Button>
              </div>
            ) : me?.test_submitted_at ? (
              <p className="text-sm text-slate-700">Your exam was submitted on {new Date(me.test_submitted_at).toLocaleString()}. A Nexus board member is reviewing it.</p>
            ) : (
              <p className="text-sm text-slate-700">Nothing requires your attention right now. A Nexus board member will be in touch if the next step requires your input.</p>
            )}
          </div>
        )}

        {test && (
          <div className="space-y-6" data-testid="applicant-test-runner">
            <div className="bg-white border border-slate-200 rounded p-4 flex items-center justify-between sticky top-0 z-10">
              <div>
                <h2 className="font-heading font-bold text-slate-900">{test.title}</h2>
                <p className="text-xs text-slate-500">{test.total} questions — {Object.keys(answers).length} answered</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Clock className="w-3 h-3" /> Single sitting
              </div>
            </div>

            {test.sections.map(sec => (
              <div key={sec.code} className="bg-white border border-slate-200 rounded">
                <div className="px-4 py-2 bg-slate-900 text-white text-xs font-mono uppercase tracking-widest rounded-t">
                  {sec.code} — {sec.name}
                </div>
                <div className="p-4 space-y-4">
                  {test.questions.filter(q => q.section_code === sec.code).map(q => (
                    <div key={q.number} data-testid={`applicant-q-${q.number}`}>
                      <p className="text-sm text-slate-900 font-medium mb-2">
                        <span className="font-mono text-slate-500 mr-2">#{q.number}</span>
                        {q.prompt}
                      </p>
                      <div className="grid gap-2">
                        {q.options.map(opt => (
                          <Radio
                            key={opt.value}
                            checked={answers[String(q.number)] === opt.value}
                            onChange={() => setAnswers(prev => ({ ...prev, [String(q.number)]: opt.value }))}
                            label={opt.label}
                            testId={`applicant-q-${q.number}-opt-${opt.value}`}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="bg-white border border-slate-200 rounded p-4 flex items-center justify-between sticky bottom-4">
              <p className="text-xs text-slate-500 flex items-center gap-2">
                <AlertCircle className="w-3 h-3" />
                Submission is final. Answer every question.
              </p>
              <Button
                onClick={submit}
                disabled={submitting || Object.keys(answers).length < test.total}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="applicant-submit-test-btn"
              >
                {submitting ? 'Submitting…' : `Submit (${Object.keys(answers).length}/${test.total})`}
              </Button>
            </div>
          </div>
        )}
      </main>
      {signingAsn && (
        <SignDocumentModal
          assignment={signingAsn}
          onClose={() => setSigningAsn(null)}
          onSigned={() => { setSigningAsn(null); fetchInbox(); }}
        />
      )}
    </div>
  );
};

export default ApplicantPortal;

/* ---------- Iter136 — Inline resubmit panel ---------- */
function ResubmitPanel({ me, token, onDone }) {
  const [values, setValues] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const fields = me?.missing_info_fields || [];
  const set = (k, v) => setValues(prev => ({ ...prev, [k]: v }));
  const submit = async () => {
    const updates = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
        .filter(([_, v]) => v !== '' && v !== undefined && v !== null)
    );
    if (Object.keys(updates).length === 0) {
      toast.error('Please fill in at least one requested field.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/applicant-portal/resubmit-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ updates }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Resubmit failed');
      } else {
        toast.success('Application resubmitted — your recruiter has been notified');
        onDone && onDone();
      }
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-amber-50 border-2 border-amber-400 rounded p-4" data-testid="applicant-resubmit-panel">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-amber-900">Additional information required</h3>
          <p className="text-sm text-amber-800 mt-1">
            Your recruiter has returned your application. Please update the fields below and resubmit — your previously submitted information stays intact.
          </p>
          {me?.missing_info_message && (
            <p className="text-sm text-amber-900 mt-2 bg-amber-100 border border-amber-300 rounded p-2 whitespace-pre-wrap">
              {me.missing_info_message}
            </p>
          )}
          {fields.length === 0 ? (
            <p className="text-xs text-amber-700 mt-3">Please contact your recruiter — no specific fields were listed.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {fields.map(f => (
                <div key={f} className="flex flex-col gap-1">
                  <label className="text-[11px] font-mono uppercase tracking-wider text-amber-900">
                    {f.replace(/_/g, ' ')}
                  </label>
                  <input
                    type="text"
                    value={values[f] || ''}
                    onChange={e => set(f, e.target.value)}
                    className="w-full px-3 py-2 border border-amber-300 rounded bg-white text-slate-900 text-sm focus:border-amber-500 focus:ring-amber-200"
                    data-testid={`resubmit-field-${f}`}
                  />
                </div>
              ))}
              <div className="pt-2">
                <Button
                  onClick={submit}
                  disabled={busy}
                  className="bg-amber-600 hover:bg-amber-500 text-white"
                  data-testid="applicant-resubmit-btn"
                >
                  {busy ? 'Submitting…' : 'Resubmit Application'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
